/**
 * Canonical money values.
 *
 * Register §7 ("Canonical integer strings"): a money value on the wire is `0`,
 * digits without leading zeros, or minus followed by positive digits. `+`, `-0`,
 * whitespace, exponents, separators and decimal money are all rejected, and a
 * JSON number is never a valid money value.
 *
 * Register §7 ("Individual money bound"): the absolute magnitude of an individual
 * money value is at most 999999999999999 minor units.
 */
/** `0`, a positive integer without leading zeros, or `-` plus such an integer. */
const CANONICAL_MINOR = /^(?:0|[1-9][0-9]*|-[1-9][0-9]*)$/;
/** Largest permitted absolute value of an individual money amount, in minor units. */
export const MAX_MINOR_MAGNITUDE = 999999999999999n;
export class InvalidMoneyError extends Error {
    name = 'InvalidMoneyError';
}
/**
 * Parse a canonical integer string into exact minor units.
 *
 * Throws {@link InvalidMoneyError} for anything that is not canonical; it never
 * falls back to a lenient parse, because a lenient parse is how a wrong amount
 * becomes a stored amount.
 */
export function parseCanonicalMinor(value) {
    if (typeof value !== 'string' || !CANONICAL_MINOR.test(value)) {
        throw new InvalidMoneyError(`not a canonical integer string: ${JSON.stringify(value)}`);
    }
    return BigInt(value);
}
/** Render exact minor units as the canonical integer string. */
export function formatCanonicalMinor(value) {
    return value.toString();
}
/** Whether the magnitude is inside the individual money bound. */
export function isWithinIndividualBound(value) {
    const magnitude = value < 0n ? -value : value;
    return magnitude <= MAX_MINOR_MAGNITUDE;
}
/** Parse a canonical integer string and reject values outside the individual bound. */
export function parseBoundedMinor(value) {
    const minor = parseCanonicalMinor(value);
    if (!isWithinIndividualBound(minor)) {
        throw new InvalidMoneyError(`outside the individual money bound: ${minor}`);
    }
    return minor;
}
//# sourceMappingURL=money.js.map